import { useMemo, useState } from "react";
import * as d3 from "d3";
import { createPortal } from "react-dom";

import type { PathwayPattern, StatusMatch } from "@thalamos/pathway-logic";

import { scale } from "../data/vizGeometry.js";
import { SECTION_COLORS, UNKNOWN_SECTION_COLOR } from "./Annotations.js";

// Status aliases for display and reverse lookup
const STATUS_ALIASES: Record<string, string> = {
  CTO: "Section 17A",
  "CTO (Recalled)": "Section 17A (Recalled)",
};

// Expand alias to canonical status
function expandAlias(status: string): string {
  return STATUS_ALIASES[status] || status;
}

// Realistic default durations for each section type (in days)
const DEFAULT_DURATIONS: Record<string, number> = {
  "Section 136": 1, // 24 hours
  "Section 5(4)": 0.25, // 6 hours
  "Section 5(2)": 3, // 72 hours
  "Section 4": 3, // 72 hours
  "Section 2": 28, // 28 days
  "Section 3": 90, // Indefinite - show ~3 months
  "Section 17A": 180, // CTO - indefinite, show ~6 months
  "Section 17A (Recalled)": 2, // 72 hours max, typically shorter
  "Not Detained": 60, // Indefinite - show ~2 months
  Expired: 30,
  Deceased: 0, // Terminal event
};

// Get realistic duration for a status
function getRealisticDuration(
  status: string,
  patternDuration?: { min?: number; max?: number },
): number {
  // If pattern specifies duration, use that
  if (patternDuration?.min || patternDuration?.max) {
    return patternDuration.min || patternDuration.max || 14;
  }
  // Otherwise use realistic defaults
  const canonical = expandAlias(status);
  return DEFAULT_DURATIONS[canonical] || 14;
}

// Get the y-level for a status
function getStatusLevel(status: string): number {
  const canonical = expandAlias(status);
  const found = scale.find(([name]) => name === canonical);
  return found ? found[1] : 0;
}

// Get color for a status
function getStatusColor(status: string): string {
  const level = getStatusLevel(status);
  return SECTION_COLORS[level] ?? UNKNOWN_SECTION_COLOR;
}

// Example pathway data point
type ExampleDataPoint = {
  day: number;
  status: string;
  level: number;
};

// Stands in wherever a pattern step names no status of its own
const WILDCARD_STATUS = "Section 2";

// Generate a representative status from a StatusMatch
function getRepresentativeStatuses(match: StatusMatch): string[] {
  switch (match.type) {
    case "exact":
      return [expandAlias(match.status)];
    case "oneOf":
      return match.statuses.map(expandAlias);
    case "any":
      return [WILDCARD_STATUS];
    case "anyExcept": {
      // Pick statuses that are not excluded
      const allStatuses = [
        "Section 136",
        "Section 5(4)",
        "Section 5(2)",
        "Section 4",
        "Section 2",
        "Section 3",
        "Section 17A",
        "Not Detained",
      ];
      const expandedExcludes = match.exclude.map(expandAlias);
      return allStatuses
        .filter((s) => !expandedExcludes.includes(s))
        .slice(0, 2);
    }
    default:
      return [WILDCARD_STATUS];
  }
}

// Generate example pathway data from a pattern
function generateExampleData(
  pattern: PathwayPattern,
  variant: number,
): ExampleDataPoint[] {
  const data: ExampleDataPoint[] = [];
  let currentDay = 0;

  for (const step of pattern.steps) {
    const isOptional =
      step.quantifier === "zeroOrOne" || step.quantifier === "zeroOrMore";

    // For variant 0 (minimal), skip optional steps
    if (variant === 0 && isOptional) {
      continue;
    }

    const statuses = getRepresentativeStatuses(step.match);
    // Use different status options for different variants. A `oneOf` with no
    // statuses, or an `anyExcept` that excludes them all, comes back empty.
    const status = statuses[variant % statuses.length] ?? WILDCARD_STATUS;
    const level = getStatusLevel(status);

    // Duration: use pattern constraint if specified, otherwise realistic defaults
    let durationDays = getRealisticDuration(status, step.duration);
    // For visualisation, cap very long durations to keep charts readable
    durationDays = Math.min(durationDays, 90);
    // Ensure at least 1 day for visibility
    durationDays = Math.max(durationDays, 1);

    // Add data points for this step (one per day for smooth line)
    for (let d = 0; d < durationDays; d++) {
      data.push({
        day: currentDay + d,
        status,
        level,
      });
    }

    currentDay += durationDays;
  }

  return data;
}

// Mini D3 visualisation component
const MiniPathwayGraph = ({
  data,
  width,
  height,
  label,
}: {
  data: ExampleDataPoint[];
  width: number;
  height: number;
  label: string;
}) => {
  const margin = { top: 8, right: 8, bottom: 20, left: 65 };
  const boundsWidth = width - margin.left - margin.right;
  const boundsHeight = height - margin.top - margin.bottom;

  if (data.length === 0) {
    return (
      <div
        style={{
          width,
          height,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#94a3b8",
        }}
      >
        No steps to visualize
      </div>
    );
  }

  const maxDay = Math.max(...data.map((d) => d.day));

  // Scales
  const xScale = d3.scaleLinear().domain([0, maxDay]).range([0, boundsWidth]);
  const yScale = d3.scaleLinear().domain([0, 9]).range([boundsHeight, 0]);

  // Build the line path with step function (vertical transitions)
  const lineBuilder = d3
    .line<ExampleDataPoint>()
    .x((d) => xScale(d.day))
    .y((d) => yScale(d.level))
    .curve(d3.curveStepAfter);

  const linePath = lineBuilder(data);

  // Create background bands for each section
  const sectionBands = [0, 1, 2, 3, 4, 5, 6, 7, 8].map((level) => ({
    level,
    y: yScale(level + 0.5),
    height: Math.abs(yScale(0) - yScale(1)),
    color: SECTION_COLORS[level],
  }));

  // Find key transition points for circles
  const transitions: { day: number; level: number; status: string }[] = [];
  let lastStatus = "";
  for (const point of data) {
    if (point.status !== lastStatus) {
      transitions.push({
        day: point.day,
        level: point.level,
        status: point.status,
      });
      lastStatus = point.status;
    }
  }
  // Add end point
  const last = data.at(-1);
  if (last !== undefined) {
    transitions.push({ day: last.day, level: last.level, status: last.status });
  }

  return (
    <div style={{ marginBottom: "1em" }}>
      <div
        style={{
          fontSize: "0.75em",
          color: "#64748b",
          marginBottom: "4px",
          fontWeight: 500,
        }}
      >
        {label}
      </div>
      <svg
        width={width}
        height={height}
        style={{
          backgroundColor: "#f8fafc",
          borderRadius: "6px",
          border: "1px solid #e2e8f0",
        }}
      >
        <g
          transform={`translate(${String(margin.left)},${String(margin.top)})`}
        >
          {/* Background bands */}
          {sectionBands.map((band) => (
            <rect
              key={band.level}
              x={0}
              y={band.y - band.height / 2}
              width={boundsWidth}
              height={band.height}
              fill={band.color}
              opacity={0.15}
            />
          ))}

          {/* Line */}
          <path
            d={linePath || undefined}
            fill="none"
            stroke="#475569"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
          />

          {/* Transition circles with hover */}
          {transitions.map((t, i) => (
            <g key={i}>
              <circle
                cx={xScale(t.day)}
                cy={yScale(t.level)}
                r={4}
                fill={getStatusColor(t.status)}
                stroke="white"
                strokeWidth={1.5}
                style={{ cursor: "pointer" }}
              />
              <title>
                {t.status} (Day {t.day})
              </title>
            </g>
          ))}

          {/* Y axis labels */}
          <g transform="translate(-4, 0)">
            {[
              { level: 0, label: "Not Det." },
              { level: 1, label: "S136" },
              { level: 2, label: "S5(4)" },
              { level: 3, label: "S5(2)" },
              { level: 4, label: "S4" },
              { level: 5, label: "S2" },
              { level: 6, label: "S3" },
              { level: 7, label: "CTO Rec." },
              { level: 8, label: "CTO" },
            ].map((item) => (
              <text
                key={item.level}
                x={0}
                y={yScale(item.level)}
                fontSize={8}
                fill="#64748b"
                textAnchor="end"
                dominantBaseline="middle"
              >
                {item.label}
              </text>
            ))}
          </g>

          {/* X axis - simple day labels */}
          <g transform={`translate(0,${String(boundsHeight)})`}>
            <line x1={0} x2={boundsWidth} y1={0} y2={0} stroke="#cbd5e1" />
            <text x={0} y={12} fontSize={9} fill="#94a3b8">
              Day 0
            </text>
            <text
              x={boundsWidth}
              y={12}
              fontSize={9}
              fill="#94a3b8"
              textAnchor="end"
            >
              Day {maxDay}
            </text>
          </g>
        </g>
      </svg>
    </div>
  );
};

// Legend component
const Legend = () => {
  const statuses = [
    { name: "Not Detained", level: 0 },
    { name: "S136", level: 1 },
    { name: "S5(4)", level: 2 },
    { name: "S5(2)", level: 3 },
    { name: "S4", level: 4 },
    { name: "S2", level: 5 },
    { name: "S3", level: 6 },
    { name: "CTO (Rec.)", level: 7 },
    { name: "CTO", level: 8 },
  ];

  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: "8px",
        marginTop: "1em",
        paddingTop: "1em",
        borderTop: "1px solid #e2e8f0",
      }}
    >
      {statuses.map(({ name, level }) => (
        <div
          key={level}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "4px",
            fontSize: "0.65em",
          }}
        >
          <div
            style={{
              width: "10px",
              height: "10px",
              backgroundColor: SECTION_COLORS[level],
              borderRadius: "2px",
            }}
          />
          <span style={{ color: "#64748b" }}>{name}</span>
        </div>
      ))}
    </div>
  );
};

// Main modal component
export const PathwayExampleModal = ({
  pattern,
  isOpen,
  onClose,
}: {
  pattern: PathwayPattern;
  isOpen: boolean;
  onClose: () => void;
}) => {
  const examples = useMemo(() => {
    const results: { data: ExampleDataPoint[]; label: string }[] = [];

    // Generate minimal example (variant 0 - skip optional)
    const minimal = generateExampleData(pattern, 0);
    if (minimal.length > 0) {
      results.push({ data: minimal, label: "Minimal match" });
    }

    // Generate full example (variant 1 - include optional)
    const full = generateExampleData(pattern, 1);
    if (full.length > 0 && JSON.stringify(full) !== JSON.stringify(minimal)) {
      results.push({ data: full, label: "With optional steps" });
    }

    // If there are oneOf options, generate alternative
    const hasOneOf = pattern.steps.some((s) => s.match.type === "oneOf");
    if (hasOneOf) {
      const alt = generateExampleData(pattern, 2);
      if (alt.length > 0) {
        results.push({ data: alt, label: "Alternative path" });
      }
    }

    return results;
  }, [pattern]);

  if (!isOpen) return null;

  return createPortal(
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: "white",
          borderRadius: "12px",
          padding: "24px",
          maxWidth: "500px",
          width: "90%",
          maxHeight: "85vh",
          overflow: "auto",
          boxShadow: "0 20px 40px rgba(0,0,0,0.2)",
        }}
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: "1em",
          }}
        >
          <div>
            <h3 style={{ margin: 0, fontSize: "1.1em", color: "#1e293b" }}>
              Example Pathways
            </h3>
            <p
              style={{
                margin: "0.25em 0 0",
                fontSize: "0.8em",
                color: "#64748b",
              }}
            >
              {pattern.name}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              fontSize: "1.5em",
              cursor: "pointer",
              color: "#94a3b8",
              padding: "0 4px",
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>

        <p
          style={{
            fontSize: "0.8em",
            color: "#64748b",
            margin: "0 0 1em",
          }}
        >
          Example patient journeys that would match this pattern:
        </p>

        {examples.length === 0 ? (
          <p style={{ color: "#64748b", fontStyle: "italic" }}>
            Unable to generate examples for this pattern.
          </p>
        ) : (
          examples.map((example, i) => (
            <MiniPathwayGraph
              key={i}
              data={example.data}
              width={440}
              height={120}
              label={example.label}
            />
          ))
        )}

        <Legend />
      </div>
    </div>,
    document.body,
  );
};

// Button to trigger the modal
export const ShowExamplesButton = ({
  pattern,
}: {
  pattern: PathwayPattern | null;
}) => {
  const [isOpen, setIsOpen] = useState(false);

  if (!pattern || pattern.steps.length === 0) {
    return null;
  }

  return (
    <>
      <button
        onClick={() => {
          setIsOpen(true);
        }}
        style={{
          padding: "4px 10px",
          fontSize: "0.8em",
          backgroundColor: "#f1f5f9",
          border: "1px solid #e2e8f0",
          borderRadius: "4px",
          cursor: "pointer",
          color: "#475569",
          display: "flex",
          alignItems: "center",
          gap: "4px",
        }}
        title="Show example pathways that would match this pattern"
      >
        <span style={{ fontSize: "1.1em" }}>📊</span>
        Show examples for this pattern
      </button>
      <PathwayExampleModal
        pattern={pattern}
        isOpen={isOpen}
        onClose={() => {
          setIsOpen(false);
        }}
      />
    </>
  );
};
